import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  Moon, Sun, LogOut, LogIn, User, Crown, Search, Coffee, BookOpen, 
  ChevronDown, Menu, Home, Brain, Newspaper, GraduationCap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { TokenDisplay } from "@/components/TokenDisplay";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "next-themes";
import { useAuth } from "@/contexts/AuthContext";
import logoLight from "@/assets/logo-light_t.png";
import logoDark from "@/assets/logo-dark_t.png";

const categoryStructure = [
  {
    name: "General",
    subcategories: [
      { name: "Health", href: "/health" },
      { name: "Lifestyle", href: "/lifestyle" },
      { name: "Other", href: "/other" }
    ]
  },
  {
    name: "Science",
    subcategories: [
      { name: "Physics", href: "/physics" },
      { name: "Chemistry", href: "/chemistry" },
      { name: "Math", href: "/math" }
    ]
  },
  {
    name: "Finance",
    subcategories: [
      { name: "Professional", href: "/finance" },
      { name: "Tax", href: "/tax" },
      { name: "Loans", href: "/loans" }
    ]
  }
];

export function Header() {
  const { theme, setTheme } = useTheme();
  const { user, signOut } = useAuth();
  const { t } = useTranslation();

  return (
    <>
      {/* Main Navigation Bar */}
      <header className="w-full">
        <div className="container flex flex-col items-center py-4 gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={logoLight} 
              alt="CalcLab" 
              className="h-16 w-auto dark:hidden"
            />
            <img 
              src={logoDark} 
              alt="CalcLab" 
              className="h-16 w-auto hidden dark:block"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex flex-wrap items-center justify-center gap-2">
            <Button variant="ghost" size="sm" asChild>
              <Link to="/search" className="flex items-center gap-1">
                <Search className="h-4 w-4" />
                {t('nav.search')}
              </Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/health">{t('categories.general.health')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/lifestyle">{t('categories.general.lifestyle')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/physics">{t('categories.science.physics')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/chemistry">{t('categories.science.chemistry')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/math">{t('categories.science.math')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/finance">{t('categories.finance.professional')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/loans">{t('categories.finance.loans')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/tax">{t('categories.finance.tax')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/tools">{t('categories.tools.title')}</Link>
            </Button>
            <Button variant="ghost" size="sm" asChild>
              <Link to="/blog" className="flex items-center gap-1">
                <Newspaper className="h-4 w-4" />
                Blog
              </Link>
            </Button>
            <Button 
              size="sm" 
              asChild
              className="relative overflow-hidden bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/50"
            >
              <Link to="/supercalc" className="font-semibold flex items-center gap-2">
                <Brain className="h-4 w-4" />
                {t('nav.superCalc')}
              </Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* Floating Actions - Desktop */}
      <div className="fixed left-4 top-4 z-50 hidden md:flex items-center gap-2 bg-card border border-border rounded-xl p-2 shadow-lg">
        <LanguageSwitcher />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setTheme(theme === "light" ? "dark" : "light")}
        >
          {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
        </Button>
        <Button
          variant="ghost"
          size="icon"
          asChild
        >
          <Link to="/">
            <Home className="h-5 w-5" />
          </Link>
        </Button>
        <Button
          variant="ghost"
          size="icon"
          asChild
        >
          <a href="https://www.buymeacoffee.com/calclab" target="_blank" rel="noopener noreferrer">
            <Coffee className="h-5 w-5" />
          </a>
        </Button>
      </div>

      {/* Floating User Menu - Desktop */}
      <div className="fixed right-4 top-4 z-50 hidden md:flex items-center gap-2">
        {/* Token Display */}
        <TokenDisplay className="shadow-lg" />
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="bg-card border border-border rounded-xl shadow-lg"
            >
              <User className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56 bg-background z-[100]">
            {user ? (
              <>
                <DropdownMenuItem asChild>
                  <Link to="/library" className="flex items-center gap-2">
                    <BookOpen className="h-4 w-4" />
                    {t('nav.library')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/learn/latex" className="flex items-center gap-2">
                    <GraduationCap className="h-4 w-4" />
                    {t('nav.latexLearning')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/membership" className="flex items-center gap-2">
                    <Crown className="h-4 w-4" />
                    {t('nav.myAccount')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => signOut()} className="flex items-center gap-2">
                  <LogOut className="h-4 w-4" />
                  {t('nav.signOut')}
                </DropdownMenuItem>
              </>
            ) : (
              <DropdownMenuItem asChild>
                <Link to="/auth" className="flex items-center gap-2">
                  <LogIn className="h-4 w-4" />
                  {t('nav.signIn')}
                </Link>
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile Menu - Fixed Top Right */}
      <div className="fixed right-4 top-4 z-50 md:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="bg-card border border-border rounded-xl shadow-lg">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] sm:w-[400px] overflow-y-auto">
            <nav className="flex flex-col gap-4 mt-8 pb-8">
              <div className="flex items-center gap-2 flex-wrap">
                <TokenDisplay compact />
                <LanguageSwitcher />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setTheme(theme === "light" ? "dark" : "light")}
                >
                  {theme === "light" ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                </Button>
              </div>
              
              <Link to="/search" className="flex items-center gap-2 text-lg">
                <Search className="h-5 w-5" />
                {t('nav.search')}
              </Link>

              <div className="space-y-2">
                <div className="text-sm font-semibold text-muted-foreground px-2">
                  {t('nav.categories')}
                </div>
                {categoryStructure.map((category) => (
                  <div key={category.name} className="space-y-1">
                    <div className="text-sm font-medium px-2 py-1">
                      {category.name}
                    </div>
                    {category.subcategories.map((sub) => (
                      <Link
                        key={sub.name}
                        to={sub.href}
                        className="block px-4 py-2 text-sm hover:bg-accent rounded-md"
                      >
                        {sub.name}
                      </Link>
                    ))}
                  </div>
                ))}
              </div>

              <Link to="/tools" className="text-lg">
                {t('categories.tools.title')}
              </Link>

              <Link to="/blog" className="flex items-center gap-2 text-lg">
                <Newspaper className="h-5 w-5" />
                Blog
              </Link>

              <Link 
                to="/supercalc" 
                className="text-lg font-semibold text-primary bg-primary/10 px-4 py-2 rounded-lg border-2 border-primary shadow-[0_0_15px_rgba(var(--primary),0.5)] hover:shadow-[0_0_25px_rgba(var(--primary),0.7)] transition-all flex items-center gap-2"
              >
                <Brain className="h-5 w-5" />
                {t('nav.superCalc')}
              </Link>

              <hr className="my-2" />

              <Link to="/" className="flex items-center gap-2 text-lg">
                <Home className="h-5 w-5" />
                {t('nav.home')}
              </Link>

              <a 
                href="https://www.buymeacoffee.com/calclab" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-lg"
              >
                <Coffee className="h-5 w-5" />
                {t('nav.donate')}
              </a>

              {user ? (
                <>
                  <Link to="/library" className="flex items-center gap-2 text-lg">
                    <BookOpen className="h-5 w-5" />
                    {t('nav.library')}
                  </Link>
                  <Link to="/learn/latex" className="flex items-center gap-2 text-lg">
                    <GraduationCap className="h-5 w-5" />
                    {t('nav.latexLearning')}
                  </Link>
                  <Link to="/membership" className="flex items-center gap-2 text-lg">
                    <Crown className="h-5 w-5" />
                    {t('nav.myAccount')}
                  </Link>
                  <button 
                    onClick={() => signOut()} 
                    className="flex items-center gap-2 text-lg text-left"
                  >
                    <LogOut className="h-5 w-5" />
                    {t('nav.signOut')}
                  </button>
                </>
              ) : (
                <Link to="/auth" className="flex items-center gap-2 text-lg">
                  <LogIn className="h-5 w-5" />
                  {t('nav.signIn')}
                </Link>
              )}
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </>
  );
}
